import type {
  Items,
  JsonObject,
  JsonValue,
  NodeExecutionContext,
  NodeInspectorSummaryRow,
  NodeOutputs,
  PollingTriggerConfig,
  TestableTriggerNode,
  TriggerNodeConfig,
  TriggerSetupContext,
  TriggerTestItemsContext,
  TypeToken,
} from "..";
import type { CredentialJsonRecord, CredentialRequirement } from "../contracts/credentialTypes";
import type { DefinedNodeCredentialAccessors, DefinedNodeCredentialBindings } from "./defineNode.types";
import type { NodeBaseOptions } from "./nodeBaseOptions.types";
import { node as persistedNode } from "../runtime-types/runtimeTypeDecorators.types";
import {
  definedNodeCredentialRequirementFactory,
  definedNodeCredentialAccessorFactory,
} from "./definePollingTriggerInternals";
import type { ZodType } from "zod";

type MaybePromise<TValue> = TValue | Promise<TValue>;

export interface DefinePollingTriggerPollContext<
  TConfig extends CredentialJsonRecord,
  TState extends JsonValue | undefined,
  TBindings extends DefinedNodeCredentialBindings | undefined,
> {
  readonly config: TConfig;
  readonly state: TState;
  readonly credentials: DefinedNodeCredentialAccessors<TBindings>;
}

export interface DefinePollingTriggerPollResult<TItemJson, TState extends JsonValue | undefined> {
  readonly items: ReadonlyArray<{ json: TItemJson; dedupKey?: string }>;
  readonly nextState: TState;
}

export type DefinePollingTriggerExecuteContext<TConfig extends TriggerNodeConfig<any, any>> =
  NodeExecutionContext<TConfig>;

export type DefinePollingTriggerTestItemsContext<TConfig extends TriggerNodeConfig<any, any>> =
  TriggerTestItemsContext<TConfig>;

export interface DefinePollingTriggerOptions<
  TKey extends string,
  TConfig extends CredentialJsonRecord,
  TItemJson,
  TState extends JsonValue | undefined,
  TBindings extends DefinedNodeCredentialBindings | undefined = undefined,
> {
  readonly key: TKey;
  readonly packageName?: string;
  readonly title: string;
  readonly description?: string;
  readonly icon?: string;
  readonly configSchema?: ZodType<TConfig>;
  readonly credentials?: TBindings;
  readonly inspectorSummary?: (
    args: Readonly<{ config: TConfig }>,
  ) => ReadonlyArray<NodeInspectorSummaryRow> | undefined;
  initialState?(): TState;
  readonly pollIntervalMs?: number;
  poll(
    pollCtx: DefinePollingTriggerPollContext<TConfig, TState, TBindings>,
  ): MaybePromise<DefinePollingTriggerPollResult<TItemJson, TState>>;
  execute?(
    items: Items<TItemJson>,
    ctx: NodeExecutionContext<DefinedPollingTriggerConfig<TConfig, TItemJson>>,
  ): MaybePromise<NodeOutputs>;
  testItems?(
    ctx: TriggerTestItemsContext<DefinedPollingTriggerConfig<TConfig, TItemJson>>,
  ): MaybePromise<Items<TItemJson>>;
}

export interface DefinedPollingTrigger<
  TKey extends string,
  TConfig extends CredentialJsonRecord,
  TItemJson,
  TState extends JsonValue | undefined,
  TBindings extends DefinedNodeCredentialBindings | undefined = undefined,
> {
  readonly kind: "defined-polling-trigger";
  readonly key: TKey;
  readonly title: string;
  readonly description?: string;
  create(
    cfg: TConfig,
    name?: string,
    idOrOptions?: string | NodeBaseOptions,
  ): DefinedPollingTriggerConfig<TConfig, TItemJson>;
  poll(
    pollCtx: Omit<DefinePollingTriggerPollContext<TConfig, TState, TBindings>, "credentials"> & {
      credentials?: DefinedNodeCredentialAccessors<TBindings>;
    },
  ): MaybePromise<DefinePollingTriggerPollResult<TItemJson, TState>>;
  register(context: { registerNode<TValue>(token: TypeToken<TValue>, implementation?: TypeToken<TValue>): void }): void;
}

export class DefinedPollingTriggerConfig<TConfig extends CredentialJsonRecord, TItemJson>
  implements TriggerNodeConfig<TItemJson, JsonValue | undefined>, PollingTriggerConfig
{
  readonly kind = "trigger" as const;
  readonly type: TypeToken<unknown>;
  readonly icon: string | undefined;
  readonly id?: string;
  readonly description?: string;

  constructor(
    public readonly name: string,
    public readonly cfg: TConfig,
    typeToken: TypeToken<unknown>,
    icon: string | undefined,
    private readonly credentialRequirements: ReadonlyArray<CredentialRequirement>,
    idOrOptions?: string | NodeBaseOptions,
    private readonly inspectorSummaryFn?: (
      args: Readonly<{ config: TConfig }>,
    ) => ReadonlyArray<NodeInspectorSummaryRow> | undefined,
    private readonly defaultPollIntervalMs?: number,
  ) {
    this.type = typeToken;
    this.icon = icon;
    const resolved = typeof idOrOptions === "string" ? { id: idOrOptions } : idOrOptions;
    this.id = resolved?.id;
    this.description = resolved?.description;
  }

  getCredentialRequirements(): ReadonlyArray<CredentialRequirement> {
    return this.credentialRequirements;
  }

  getTriggerPollConfig(): Readonly<{ config: JsonObject; pollIntervalMs?: number }> {
    const cfgPollInterval = (this.cfg as Partial<{ pollIntervalMs: number }>).pollIntervalMs;
    const resolvedInterval = cfgPollInterval ?? this.defaultPollIntervalMs;
    return {
      config: this.cfg as unknown as JsonObject,
      pollIntervalMs: resolvedInterval,
    };
  }

  inspectorSummary(): ReadonlyArray<NodeInspectorSummaryRow> | undefined {
    return this.inspectorSummaryFn?.({ config: this.cfg });
  }
}

interface InternalWrappedState {
  readonly userState: JsonValue | undefined;
  readonly seenKeys: ReadonlyArray<string>;
}

function isWrappedState(value: unknown): value is InternalWrappedState {
  return (
    value !== null &&
    typeof value === "object" &&
    "seenKeys" in (value as Record<string, unknown>) &&
    Array.isArray((value as InternalWrappedState).seenKeys)
  );
}

export function definePollingTrigger<
  TKey extends string,
  TConfig extends CredentialJsonRecord,
  TItemJson,
  TState extends JsonValue | undefined,
  TBindings extends DefinedNodeCredentialBindings | undefined = undefined,
>(
  options: DefinePollingTriggerOptions<TKey, TConfig, TItemJson, TState, TBindings>,
): DefinedPollingTrigger<TKey, TConfig, TItemJson, TState, TBindings> {
  const credentialRequirements = definedNodeCredentialRequirementFactory.create(options.credentials);
  const DEFAULT_INTERVAL_MS = 60_000;

  type TConfig_ = DefinedPollingTriggerConfig<TConfig, TItemJson>;

  const DefinedPollingTriggerRuntime = class implements TestableTriggerNode<TConfig_> {
    readonly kind = "trigger" as const;
    readonly outputPorts = ["main"] as const;

    async setup(ctx: TriggerSetupContext<TConfig_, JsonValue | undefined>): Promise<JsonValue | undefined> {
      const cfg = ctx.config.cfg;
      const intervalMs =
        (cfg as Partial<{ pollIntervalMs: number }>).pollIntervalMs ?? options.pollIntervalMs ?? DEFAULT_INTERVAL_MS;

      const persisted = ctx.previousState;
      const existingWrapped: InternalWrappedState | undefined = isWrappedState(persisted) ? persisted : undefined;
      const seedWrapped: InternalWrappedState = existingWrapped ?? {
        userState: options.initialState ? options.initialState() : undefined,
        seenKeys: [],
      };

      const result = await ctx.polling.start<InternalWrappedState, TItemJson>({
        intervalMs,
        seedState: seedWrapped,
        runCycle: async ({ previousState }) => {
          const wrapped: InternalWrappedState = previousState ?? seedWrapped;
          const seenSet = new Set(wrapped.seenKeys);

          const credentialAccessors = definedNodeCredentialAccessorFactory.create(
            options.credentials,
            ctx,
          ) as DefinedNodeCredentialAccessors<TBindings>;

          const pollResult = await options.poll({
            config: cfg,
            state: wrapped.userState as TState,
            credentials: credentialAccessors,
          });

          const newItems: Array<{ json: TItemJson }> = [];
          const newKeys: string[] = [];
          for (const item of pollResult.items) {
            if (item.dedupKey !== undefined) {
              if (seenSet.has(item.dedupKey)) {
                continue;
              }
              newKeys.push(item.dedupKey);
            }
            newItems.push({ json: item.json });
          }

          const allKeys = [...wrapped.seenKeys, ...newKeys];
          const cappedKeys = allKeys.length > 2000 ? allKeys.slice(allKeys.length - 2000) : allKeys;

          const nextWrapped: InternalWrappedState = {
            userState: pollResult.nextState,
            seenKeys: cappedKeys,
          };

          return {
            items: newItems as Items<TItemJson>,
            nextState: nextWrapped,
          };
        },
      });

      return result as JsonValue | undefined;
    }

    async execute(items: Items<TItemJson>, ctx: NodeExecutionContext<TConfig_>): Promise<NodeOutputs> {
      if (options.execute) {
        return await options.execute(items, ctx);
      }
      return { main: items };
    }

    async getTestItems(ctx: TriggerTestItemsContext<TConfig_>): Promise<Items> {
      if (options.testItems) {
        return await options.testItems(ctx);
      }
      return [];
    }
  };

  persistedNode({ name: options.key, packageName: options.packageName })(DefinedPollingTriggerRuntime);

  const definition: DefinedPollingTrigger<TKey, TConfig, TItemJson, TState, TBindings> = {
    kind: "defined-polling-trigger",
    key: options.key,
    title: options.title,
    description: options.description,

    create(
      cfg: TConfig,
      name = options.title,
      idOrOptions?: string | NodeBaseOptions,
    ): DefinedPollingTriggerConfig<TConfig, TItemJson> {
      return new DefinedPollingTriggerConfig<TConfig, TItemJson>(
        name,
        cfg,
        DefinedPollingTriggerRuntime,
        options.icon,
        credentialRequirements,
        idOrOptions,
        options.inspectorSummary as
          | ((args: Readonly<{ config: TConfig }>) => ReadonlyArray<NodeInspectorSummaryRow> | undefined)
          | undefined,
        options.pollIntervalMs,
      );
    },

    poll(pollCtx) {
      return options.poll({
        config: pollCtx.config,
        state: pollCtx.state,
        credentials: (pollCtx.credentials ?? {}) as DefinedNodeCredentialAccessors<TBindings>,
      });
    },

    register(context) {
      context.registerNode(DefinedPollingTriggerRuntime);
    },
  };

  return definition;
}
